/**
 * PnLChatWidget.js — v5.0
 * Added: Chat history sidebar (like Claude's sidebar)
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { InlineChart, detectChart } from './ChatCharts'

const API_BASE       = (process.env.REACT_APP_API_URL || '')
const TOKEN_KEY      = 'pnl_chat_token'
const LOGIN_EMAIL    = 'admin@tsi.com'
const LOGIN_PASSWORD = 'ChangeMe123!'

const SUGGESTIONS = [
  { emoji: '📈', label: 'Period Summary',   desc: 'Quick P&L overview for the latest month',       prompt: 'Summarize the P&L for the latest available month' },
  { emoji: '🚨', label: 'Top Variances',    desc: 'Spot items off forecast by more than 10%',      prompt: 'Which expenses exceeded forecast by more than 10% in the latest month?' },
  { emoji: '🔄', label: 'Compare Months',   desc: 'Month-over-month revenue changes',              prompt: 'Compare Gross Revenue between the last two months' },
  { emoji: '📋', label: 'Full P&L Review',  desc: 'Summary with variance explanations & actions',  prompt: 'Give me a full P&L summary for the latest month including variance explanations and proposed actions for each significant variance' },
  { emoji: '🎯', label: 'Action Plan',      desc: 'Structured plan to address key issues',         prompt: 'Provide a complete P&L review for the latest period with detailed variance analysis and a structured action plan' },
  { emoji: '💡', label: 'Revenue Insights', desc: 'Biggest revenue line items breakdown',          prompt: 'What were our biggest revenue line items and how do they compare to forecast?' },
]

// ── Auth ─────────────────────────────────────────────────────────────────────
function getToken()   { return localStorage.getItem(TOKEN_KEY) }
function saveToken(t) { localStorage.setItem(TOKEN_KEY, t) }
function clearToken() { localStorage.removeItem(TOKEN_KEY) }

async function ensureAuth() {
  if (getToken()) return
  const form = new URLSearchParams()
  form.append('username', LOGIN_EMAIL)
  form.append('password', LOGIN_PASSWORD)
  const res = await fetch(API_BASE + '/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: form.toString(),
  })
  const data = await res.json()
  if (data?.access_token) saveToken(data.access_token)
  else throw new Error(data?.detail || 'Login failed')
}

async function apiFetch(path, options = {}) {
  const token = getToken()
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) }
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(API_BASE + path, { ...options, headers })
  if (res.status === 204) return null
  const text = await res.text()
  let data
  try { data = JSON.parse(text) } catch { data = text }
  if (!res.ok) throw new Error(data?.detail || res.statusText)
  return data
}

// ── Column config ─────────────────────────────────────────────────────────────
const PCT_COLS = new Set(['periodic_var_vs_fcst_pct','periodic_var_vs_prior_month_pct','periodic_var_vs_prior_year_pct','ytd_var_vs_fcst_pct','ytd_var_vs_fcstdec_pct','ytd_var_vs_prior_year_pct'])
const NUM_COLS = new Set(['periodic_actual','periodic_forecast','periodic_prior_month','periodic_prior_year','periodic_var_vs_fcst','ytd_actual','ytd_forecast','ytd_prior_year','ytd_var_vs_fcst','ytd_var_vs_prior_year'])
const COL_LABELS = { line_item:'Line Item', period:'Period', department:'Dept', inc_exp:'Type', periodic_actual:'Actual', periodic_forecast:'Forecast', periodic_prior_month:'Prior Mo', periodic_prior_year:'Prior Yr', periodic_var_vs_fcst:'Var vs Fcst', periodic_var_vs_fcst_pct:'Var %', ytd_actual:'YTD Actual', ytd_forecast:'YTD Fcst', ytd_var_vs_fcst:'YTD Var', ytd_var_vs_fcst_pct:'YTD Var %' }

function fmtCell(col, val) {
  if (val === null || val === undefined || val === '') return { text: '—', color: null }
  if (PCT_COLS.has(col)) {
    const n = Number(val); if (isNaN(n)) return { text: String(val), color: null }
    return { text: `${n > 0 ? '+' : ''}${n.toFixed(1)}%`, color: n > 0 ? '#16a34a' : n < 0 ? '#dc2626' : null }
  }
  if (NUM_COLS.has(col)) {
    const n = Number(val); if (isNaN(n)) return { text: String(val), color: null }
    const isVar = col.includes('var')
    return { text: `${isVar && n > 0 ? '+' : ''}${n.toLocaleString('en-US', { maximumFractionDigits: 1 })}`, color: isVar ? (n > 0 ? '#16a34a' : n < 0 ? '#dc2626' : null) : null }
  }
  return { text: String(val), color: null }
}

// ── DataTable ─────────────────────────────────────────────────────────────────
function DataTable({ data, large }) {
  const [open, setOpen] = useState(false)
  const cols = Object.keys(data[0]).filter(c => COL_LABELS[c] || NUM_COLS.has(c) || PCT_COLS.has(c))
  const display = cols.length > 0 ? cols : Object.keys(data[0]).slice(0, 8)
  return (
    <div style={{ marginTop: 10, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderBottom: open ? '1px solid #e2e8f0' : 'none' }}>
        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>🗂 Source data · {data.length} row{data.length > 1 ? 's' : ''}</span>
        <button onClick={() => setOpen(o => !o)} style={{ background: open ? '#f97316' : '#fff', border: '1px solid ' + (open ? '#f97316' : '#e2e8f0'), color: open ? '#fff' : '#64748b', borderRadius: 6, padding: '2px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: large ? 12 : 11, width: '100%', minWidth: 300 }}>
            <thead><tr style={{ background: '#f8fafc' }}>{display.map(c => <th key={c} style={{ padding: '6px 10px', textAlign: 'left', color: '#94a3b8', fontWeight: 700, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{COL_LABELS[c] ?? c}</th>)}</tr></thead>
            <tbody>{data.map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                {display.map(c => { const { text, color } = fmtCell(c, row[c]); return <td key={c} style={{ padding: '5px 10px', whiteSpace: 'nowrap', color: color || '#334155', textAlign: NUM_COLS.has(c) || PCT_COLS.has(c) ? 'right' : 'left', fontWeight: color ? 700 : 400 }}>{text}</td> })}
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Inline text formatter ─────────────────────────────────────────────────────
function inFmt(text) {
  const parts = []; const re = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g; let last = 0, m
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index))
    if (m[0].startsWith('**'))      parts.push(<strong key={m.index} style={{ color: '#0f172a', fontWeight: 700 }}>{m[2]}</strong>)
    else if (m[0].startsWith('*')) parts.push(<em key={m.index} style={{ color: '#475569' }}>{m[3]}</em>)
    else parts.push(<code key={m.index} style={{ background: '#fef3ec', padding: '1px 5px', borderRadius: 4, fontSize: '0.88em', fontFamily: 'monospace', color: '#ea580c', border: '1px solid #fed7aa' }}>{m[4]}</code>)
    last = m.index + m[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts
}

// ── Markdown renderer ─────────────────────────────────────────────────────────
function MD({ text, large }) {
  if (!text) return null
  const fs = large ? 14 : 13

  function parseTable(lines, startIdx) {
    const rows = []
    let i = startIdx
    while (i < lines.length) {
      const line = lines[i].trim()
      if (!line.startsWith('|')) break
      const cells = line.split('|').filter((_, idx, arr) => idx > 0 && idx < arr.length - 1).map(c => c.trim())
      if (!cells.every(c => /^[-: ]+$/.test(c))) rows.push(cells)
      i++
    }
    return { rows, endIdx: i }
  }

  function renderTable(rows, key) {
    if (rows.length === 0) return null
    const header = rows[0]
    const body = rows.slice(1)
    return (
      <div key={key} style={{ overflowX: 'auto', margin: '10px 0', borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: large ? 13 : 12 }}>
          <thead>
            <tr style={{ background: 'linear-gradient(135deg,#fff7ed,#ffedd5)', borderBottom: '2px solid #fed7aa' }}>
              {header.map((cell, ci) => (
                <th key={ci} style={{ padding: large ? '10px 14px' : '8px 12px', textAlign: 'left', fontWeight: 700, color: '#ea580c', whiteSpace: 'nowrap', fontSize: large ? 12 : 11, letterSpacing: 0.2 }}>{cell}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, ri) => (
              <tr key={ri}
                style={{ background: ri % 2 === 0 ? '#ffffff' : '#f8fafc', borderBottom: '1px solid #f1f5f9', transition: 'background 0.12s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fff7ed'}
                onMouseLeave={e => e.currentTarget.style.background = ri % 2 === 0 ? '#ffffff' : '#f8fafc'}>
                {row.map((cell, ci) => {
                  const isPos = cell.startsWith('+') && cell !== '—'
                  const isNeg = cell.startsWith('-') && cell !== '—' && !cell.startsWith('--')
                  const isNum = /^[$+\-]?[\d,.]+[KMB%]?$/.test(cell.replace(/\s/g,''))
                  return (
                    <td key={ci} style={{ padding: large ? '9px 14px' : '7px 12px', textAlign: isNum || ci > 0 ? 'right' : 'left', color: isPos ? '#16a34a' : isNeg ? '#dc2626' : ci === 0 ? '#1e293b' : '#334155', fontWeight: isPos || isNeg ? 700 : ci === 0 ? 600 : 400, whiteSpace: 'nowrap', fontFamily: isNum ? '"JetBrains Mono",monospace' : 'inherit', fontSize: large ? 13 : 12 }}>
                      {inFmt(cell || '—')}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const lines = text.split('\n')
  const elements = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (line.trim().startsWith('|')) {
      const { rows, endIdx } = parseTable(lines, i)
      if (rows.length > 0) { elements.push(renderTable(rows, `table-${i}`)); i = endIdx; continue }
    }
    if (line.startsWith('### ')) elements.push(<div key={i} style={{ fontWeight: 700, fontSize: fs + 1, color: '#1e293b', margin: '12px 0 5px' }}>{inFmt(line.slice(4))}</div>)
    else if (line.startsWith('## ')) elements.push(<div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '16px 0 8px' }}><div style={{ width: 3, height: fs + 6, background: 'linear-gradient(180deg,#f97316,#ea580c)', borderRadius: 2, flexShrink: 0 }} /><span style={{ fontWeight: 800, fontSize: fs + 2, color: '#ea580c', letterSpacing: -0.2 }}>{inFmt(line.slice(3))}</span></div>)
    else if (line.startsWith('# ')) elements.push(<div key={i} style={{ fontWeight: 800, fontSize: fs + 3, color: '#f97316', margin: '16px 0 8px' }}>{inFmt(line.slice(2))}</div>)
    else if (line.startsWith('- ') || line.startsWith('* ')) elements.push(<div key={i} style={{ display: 'flex', gap: 8, marginBottom: 5, alignItems: 'flex-start' }}><span style={{ color: '#f97316', fontWeight: 700, flexShrink: 0, marginTop: 2 }}>•</span><span style={{ fontSize: fs, color: '#334155', lineHeight: 1.6 }}>{inFmt(line.slice(2))}</span></div>)
    else if (/^\d+\. /.test(line)) { const num = line.match(/^(\d+)\. /)[1]; elements.push(<div key={i} style={{ display: 'flex', gap: 10, marginBottom: 6, alignItems: 'flex-start' }}><span style={{ background: 'linear-gradient(135deg,#f97316,#ea580c)', color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{num}</span><span style={{ fontSize: fs, color: '#334155', lineHeight: 1.6 }}>{inFmt(line.replace(/^\d+\. /, ''))}</span></div>) }
    else if (line.startsWith('---')) elements.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '10px 0' }} />)
    else if (!line.trim()) elements.push(<div key={i} style={{ height: 5 }} />)
    else elements.push(<div key={i} style={{ marginBottom: 3, lineHeight: 1.65, fontSize: fs, color: '#334155' }}>{inFmt(line)}</div>)
    i++
  }
  return <div>{elements}</div>
}

// ── Bubble ────────────────────────────────────────────────────────────────────
function Bubble({ msg, large }) {
  const [copied, setCopied] = useState(false)
  if (msg.role === 'user') return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: large ? 18 : 12 }}>
      <div style={{ maxWidth: large ? '65%' : '82%', padding: large ? '12px 18px' : '9px 14px', background: 'linear-gradient(135deg,#f97316,#ea580c)', color: '#fff', borderRadius: '16px 16px 4px 16px', fontSize: large ? 14 : 13, lineHeight: 1.55, boxShadow: '0 3px 12px rgba(249,115,22,0.28)', fontWeight: 500 }}>
        {msg.content}
      </div>
    </div>
  )
  return (
    <div style={{ display: 'flex', gap: large ? 12 : 9, marginBottom: large ? 20 : 14, alignItems: 'flex-start' }}>
      <div style={{ width: large ? 36 : 30, height: large ? 36 : 30, borderRadius: '50%', background: 'linear-gradient(135deg,#fff7ed,#ffedd5)', border: '2px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: large ? 17 : 14, flexShrink: 0 }}>🤖</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: large ? '4px 16px 16px 16px' : '4px 14px 14px 14px', padding: large ? '14px 18px' : '10px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <MD text={msg.content} large={large} />
          {msg.data && msg.data.length > 0 && <DataTable data={msg.data} large={large} />}
          {/* ── CHART ADDITION (new) ── */}
          {msg.chartConfig && <InlineChart chartConfig={msg.chartConfig} large={large} />}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center', paddingLeft: 2 }}>
          {msg.elapsed_ms && <span style={{ fontSize: 10, color: '#94a3b8' }}>{(msg.elapsed_ms / 1000).toFixed(1)}s</span>}
          <button onClick={() => { navigator.clipboard.writeText(msg.content); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
            style={{ background: 'none', border: '1px solid #e2e8f0', color: copied ? '#16a34a' : '#94a3b8', borderRadius: 6, padding: '1px 8px', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>
            {copied ? '✓ Copied' : '📋 Copy'}
          </button>
        </div>
      </div>
    </div>
  )
}

function TypingDots({ large }) {
  return (
    <div style={{ display: 'flex', gap: large ? 12 : 9, alignItems: 'center', marginBottom: large ? 18 : 12 }}>
      <div style={{ width: large ? 36 : 30, height: large ? 36 : 30, borderRadius: '50%', background: 'linear-gradient(135deg,#fff7ed,#ffedd5)', border: '2px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: large ? 17 : 14 }}>🤖</div>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px 14px 14px 14px', padding: '10px 14px', display: 'flex', gap: 5, alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        {[0,1,2].map(i => <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: '#f97316', animation: 'pnl-bounce 1.2s ease-in-out infinite', animationDelay: `${i * 0.18}s` }} />)}
        <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>Thinking…</span>
      </div>
    </div>
  )
}

// ── Window dots ───────────────────────────────────────────────────────────────
function WinDots({ onClose, onMinimize, onMaximize, isMaximized }) {
  const dots = [
    { key: 'close', bg: '#ff5f57', border: '#e0443e', icon: '✕', action: onClose,    title: 'Close' },
    { key: 'min',   bg: '#febc2e', border: '#d6a023', icon: '−', action: onMinimize, title: 'Minimize' },
    { key: 'max',   bg: '#28c840', border: '#1aab29', icon: isMaximized ? '⊡' : '⊞', action: onMaximize, title: isMaximized ? 'Restore' : 'Maximize' },
  ]
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      {dots.map(d => (
        <button key={d.key} onClick={d.action} title={d.title}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.18)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          style={{ width: 16, height: 16, borderRadius: '50%', background: d.bg, border: `1.5px solid ${d.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, transition: 'transform 0.12s', flexShrink: 0 }}>
          <span style={{ fontSize: 9, fontWeight: 900, color: 'rgba(0,0,0,0.5)', lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>{d.icon}</span>
        </button>
      ))}
    </div>
  )
}

// ── Welcome screen ────────────────────────────────────────────────────────────
function Welcome({ onSend, large }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: large ? 40 : 18, paddingBottom: 20, textAlign: 'center' }}>
      <div style={{ width: large ? 76 : 58, height: large ? 76 : 58, borderRadius: '50%', background: 'linear-gradient(135deg,#fff7ed,#ffedd5)', border: '2.5px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: large ? 38 : 28, marginBottom: 14, boxShadow: '0 6px 20px rgba(249,115,22,0.18)' }}>🤖</div>
      <h2 style={{ margin: '0 0 6px', fontSize: large ? 22 : 16, fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>IRIS</h2>
      <p style={{ margin: '0 0 24px', fontSize: large ? 14 : 12, color: '#94a3b8', maxWidth: 300, lineHeight: 1.5 }}>Insight &amp; Reporting for Income &amp; Spend</p>
      <div style={{ display: 'grid', gridTemplateColumns: large ? 'repeat(3,1fr)' : 'repeat(2,1fr)', gap: large ? 12 : 8, width: '100%', maxWidth: large ? 760 : '100%' }}>
        {SUGGESTIONS.map(sg => (
          <button key={sg.label} onClick={() => onSend(sg.prompt)}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 5, padding: large ? '15px 16px' : '10px 11px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, cursor: 'pointer', textAlign: 'left', transition: 'all 0.18s', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(249,115,22,0.14)'; e.currentTarget.style.transform = 'translateY(-2px)' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'none' }}>
            <span style={{ fontSize: large ? 24 : 19 }}>{sg.emoji}</span>
            <div style={{ fontSize: large ? 13 : 11, fontWeight: 700, color: '#1e293b' }}>{sg.label}</div>
            <div style={{ fontSize: large ? 11 : 10, color: '#94a3b8', lineHeight: 1.4 }}>{sg.desc}</div>
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Chat History Sidebar ──────────────────────────────────────────────────────
function ChatSidebar({ sessions, activeId, onSelect, onNew, onDelete, loading, onToggle }) {
  const fmtDate = (d) => {
    const date = new Date(d)
    const now = new Date()
    const diff = now - date
    if (diff < 60000) return 'Just now'
    if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`
    if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`
    return date.toLocaleDateString('en-GB', { day:'2-digit', month:'short' })
  }

  return (
    <div style={{ width: 240, flexShrink: 0, background: '#fafafa', display: 'flex', flexDirection: 'column', borderRight: '1px solid #e2e8f0', height: '100%' }}>
      {/* Header */}
      <div style={{ padding: '16px 14px 12px', borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#f97316,#ea580c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🤖</div>
            <div><span style={{ fontWeight: 800, fontSize: 13, color: '#1e293b', letterSpacing: -0.3 }}>IRIS</span><div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>Chat History</div></div>
          </div>
          <button onClick={onToggle}
            style={{ width: 28, height: 28, borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#f97316', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, transition: 'all 0.15s', flexShrink: 0 }}
            onMouseEnter={e => { e.currentTarget.style.background = '#fff7ed'; e.currentTarget.style.borderColor = '#f97316' }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0' }}
            title="Hide sidebar">
            ◀
          </button>
        </div>
        {/* New Chat button */}
        <button onClick={onNew}
          style={{ width: '100%', padding: '9px 12px', background: 'linear-gradient(135deg,#f97316,#ea580c)', border: 'none', borderRadius: 9, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, transition: 'all 0.15s', boxShadow: '0 3px 10px rgba(249,115,22,0.3)' }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> New chat
        </button>
      </div>

      {/* Sessions list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 11 }}>Loading...</div>
        )}
        {!loading && sessions.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px 8px', color: '#94a3b8', fontSize: 11, lineHeight: 1.5 }}>
            No chats yet.<br/>Start a conversation!
          </div>
        )}
        {sessions.map(s => (
          <div key={s.id}
            onClick={() => onSelect(s.id)}
            style={{ padding: '9px 10px', borderRadius: 9, cursor: 'pointer', marginBottom: 3, background: activeId === s.id ? '#fff7ed' : 'transparent', border: activeId === s.id ? '1.5px solid #fed7aa' : '1.5px solid transparent', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 8 }}
            onMouseEnter={e => { if (activeId !== s.id) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0' } }}
            onMouseLeave={e => { if (activeId !== s.id) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent' } }}>
            <span style={{ fontSize: 13, flexShrink: 0 }}>💬</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: activeId === s.id ? '#ea580c' : '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>
                {s.title || 'Chat'}
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{fmtDate(s.updated_at)}</div>
            </div>
            <button
              onClick={e => { e.stopPropagation(); onDelete(s.id) }}
              style={{ width: 22, height: 22, borderRadius: 5, border: 'none', background: 'transparent', color: '#cbd5e1', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#ef4444' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#cbd5e1' }}
              title="Delete chat">
              🗑
            </button>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 14px', borderTop: '1px solid #e2e8f0', background: '#fff' }}>
        <div style={{ fontSize: 9, color: '#cbd5e1', letterSpacing: 1, textTransform: 'uppercase' }}>TSI IRIS v5.0</div>
      </div>
    </div>
  )
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function PnLChatWidget() {
  const [view, setView]               = useState('closed')
  const [messages, setMessages]       = useState([])
  const [loading, setLoading]         = useState(false)
  const [sessionId, setSessionId]     = useState(null)
  const [input, setInput]             = useState('')
  const [authReady, setAuthReady]     = useState(false)
  const [authError, setAuthError]     = useState(null)
  const [sessions, setSessions]       = useState([])
  const [sessLoading, setSessLoading] = useState(false)
  const [showSidebar, setShowSidebar] = useState(true)
  const bottomRef = useRef(null)
  const taRef     = useRef(null)
  const maxTaRef  = useRef(null)

  useEffect(() => {
    ensureAuth().then(() => setAuthReady(true)).catch(e => setAuthError(e.message))
  }, [])

  useEffect(() => {
    if (view !== 'closed' && authReady) loadSessions()
  }, [view, authReady])

  // Ref for the messages scroll container
  const messagesContainerRef = useRef(null)

  useEffect(() => {
    if (view === 'minimized') return
    setTimeout(() => {
      // Scroll only within the chat container — NOT the whole page
      const container = messagesContainerRef.current
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    }, 60)
  }, [messages, loading, view])

  const growTa = useCallback((ref) => {
    const ta = ref?.current; if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = Math.min(ta.scrollHeight, 140) + 'px'
  }, [])
  useEffect(() => { growTa(taRef); growTa(maxTaRef) }, [input, growTa])

  const loadSessions = async () => {
    setSessLoading(true)
    try {
      const data = await apiFetch('/api/chat/sessions')
      setSessions(data.sessions || [])
    } catch(e) { console.warn('Could not load sessions:', e.message) }
    finally { setSessLoading(false) }
  }

  const loadSession = async (id) => {
    try {
      const data = await apiFetch(`/api/chat/sessions/${id}`)
      const msgs = data.messages.map(m => ({ role: m.role, content: m.content }))
      setMessages(msgs)
      setSessionId(id)
    } catch(e) { console.warn('Could not load session:', e.message) }
  }

  const deleteSession = async (id) => {
    try {
      await apiFetch(`/api/chat/sessions/${id}`, { method: 'DELETE' })
      setSessions(prev => prev.filter(s => s.id !== id))
      if (sessionId === id) { setMessages([]); setSessionId(null) }
    } catch(e) { console.warn('Could not delete session:', e.message) }
  }

  const send = async (question) => {
    if (!question.trim() || loading) return
    setInput('')
    if (view === 'minimized') setView('normal')
    setMessages(prev => [...prev, { role: 'user', content: question }])
    setLoading(true)
    try {
      await ensureAuth()
      const data = await apiFetch('/api/chat', {
        method: 'POST',
        body: JSON.stringify({ question, session_id: sessionId }),
      })
      const newSessionId = data.session_id
      if (!sessionId && newSessionId) {
        setSessionId(newSessionId)
        setSessions(prev => [{
          id: newSessionId,
          title: question.slice(0, 60),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }, ...prev])
      } else if (sessionId) {
        setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, updated_at: new Date().toISOString() } : s))
      }
      // ── CHART ADDITION: detect chart from question + response ──
      const chartConfig = detectChart(question, data)
      setMessages(prev => [...prev, { role: 'assistant', content: data.answer, tools_used: data.tools_used, data: data.data, elapsed_ms: data.elapsed_ms, chartConfig }])
    } catch (err) {
      if (err.message?.includes('401') || err.message?.includes('expired')) {
        clearToken()
        try {
          await ensureAuth()
          const data = await apiFetch('/api/chat', { method: 'POST', body: JSON.stringify({ question, session_id: sessionId }) })
          setMessages(prev => [...prev, { role: 'assistant', content: data.answer, data: data.data, elapsed_ms: data.elapsed_ms }])
        } catch {
          setMessages(prev => [...prev, { role: 'assistant', content: '🤔 Session expired. Please try again.' }])
        }
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: `😕 Something went wrong: ${err.message || 'Please try again.'}` }])
      }
    } finally { setLoading(false) }
  }

  const handleKey = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      e.stopPropagation()
      const val = e.target.value.trim()
      if (val && !loading) send(val)
    }
  }, [loading, send])

  const newChat = () => { setMessages([]); setSessionId(null) }

  const isMax    = view === 'maximized'
  const isMin    = view === 'minimized'
  const isNormal = view === 'normal'
  const FONT     = '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif'

  const HeaderBar = ({ large }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: large ? '14px 28px' : '12px 16px', background: 'linear-gradient(135deg,#fff7ed,#ffedd5)', borderBottom: '1px solid #fed7aa', flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <WinDots
          onClose={() => setView('closed')}
          onMinimize={() => setView(isMin ? 'normal' : 'minimized')}
          onMaximize={() => setView(isMax ? 'normal' : 'maximized')}
          isMaximized={isMax}
        />
        <div style={{ width: 1, height: 22, background: '#fed7aa' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: large ? 38 : 32, height: large ? 38 : 32, borderRadius: '50%', background: 'linear-gradient(135deg,#f97316,#ea580c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: large ? 19 : 16, boxShadow: '0 3px 10px rgba(249,115,22,0.3)', flexShrink: 0 }}>🤖</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: large ? 16 : 13, color: '#1e293b' }}>IRIS</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 1 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: authReady ? '#22c55e' : '#f59e0b', display: 'inline-block' }} />
              <span style={{ fontSize: large ? 11 : 10, color: authReady ? '#16a34a' : '#92400e', fontWeight: 600 }}>
                {authReady ? 'Connected to live data' : 'Connecting…'}
              </span>
            </div>
          </div>
        </div>
      </div>
      {messages.length > 0 && (
        <button onClick={newChat}
          style={{ background: '#fff', border: '1.5px solid #fed7aa', color: '#ea580c', borderRadius: 8, padding: large ? '6px 16px' : '4px 12px', fontSize: large ? 12 : 11, cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}
          onMouseEnter={e => e.currentTarget.style.background = '#fff7ed'}
          onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
          ✦ New chat
        </button>
      )}
    </div>
  )

  const MessagesArea = ({ large }) => (
    <div ref={messagesContainerRef} style={{ flex: 1, overflowY: 'auto', padding: large ? '28px 40px 16px' : '14px 14px 8px', background: '#f8fafc', scrollbarWidth: 'thin', scrollbarColor: '#e2e8f0 transparent' }}>
      {authError && (
        <div style={{ padding: '10px 14px', background: '#fef2f2', borderRadius: 10, border: '1px solid #fecaca', marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>⚠ Auth error: {authError}</span>
        </div>
      )}
      {messages.length === 0
        ? <Welcome onSend={send} large={large} />
        : (
          <div style={{ maxWidth: large ? 840 : '100%', margin: large ? '0 auto' : 0 }}>
            {messages.map((m, i) => <Bubble key={i} msg={m} large={large} />)}
            {loading && <TypingDots large={large} />}
            <div ref={bottomRef} />
          </div>
        )
      }
    </div>
  )

  return (
    <>
      {view === 'closed' && (
        <button onClick={() => setView('normal')}
          style={{ position: 'fixed', bottom: 28, right: 28, zIndex: 9999, display: 'flex', alignItems: 'center', gap: 10, background: 'linear-gradient(135deg,#f97316,#ea580c)', color: '#fff', border: 'none', borderRadius: 50, padding: '13px 22px', fontSize: 20, cursor: 'pointer', boxShadow: '0 6px 24px rgba(249,115,22,0.5)', transition: 'all 0.2s', fontFamily: FONT }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(249,115,22,0.55)' }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(249,115,22,0.5)' }}>
          🤖 <span style={{ fontSize: 14, fontWeight: 700 }}>Ask IRIS</span>
        </button>
      )}

      {isMin && (
        <div style={{ position: 'fixed', bottom: 28, right: 28, zIndex: 9998, fontFamily: FONT }} onClick={() => setView('normal')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'linear-gradient(135deg,#fff7ed,#ffedd5)', border: '1px solid #fed7aa', borderRadius: 14, padding: '10px 18px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', cursor: 'pointer' }}>
            <div onClick={e => e.stopPropagation()}>
              <WinDots onClose={() => setView('closed')} onMinimize={() => setView('normal')} onMaximize={() => setView('maximized')} isMaximized={false} />
            </div>
            <div style={{ width: 1, height: 18, background: '#fed7aa' }} />
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'linear-gradient(135deg,#f97316,#ea580c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>🤖</div>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>IRIS</span>
            {messages.filter(m => m.role === 'assistant').length > 0 && (
              <span style={{ background: 'linear-gradient(135deg,#f97316,#ea580c)', color: '#fff', borderRadius: 999, padding: '1px 8px', fontSize: 10, fontWeight: 700 }}>
                {messages.filter(m => m.role === 'assistant').length}
              </span>
            )}
            <span style={{ fontSize: 11, color: '#94a3b8' }}>Click to restore</span>
          </div>
        </div>
      )}

      {isNormal && (
        <div style={{ position: 'fixed', bottom: 28, right: 28, zIndex: 9998, width: 400, height: 'calc(100vh - 100px)', maxHeight: 580, minHeight: 420, display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.13),0 4px 16px rgba(249,115,22,0.08)', fontFamily: FONT }}>
          <HeaderBar large={false} />
          <MessagesArea large={false} />
          <div style={{ padding: '10px 14px 14px', borderTop: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, background: '#fff', border: '2px solid ' + (input.trim() ? '#f97316' : '#e2e8f0'), borderRadius: 14, padding: '10px 10px 10px 16px', transition: 'border-color 0.2s', boxShadow: input.trim() ? '0 0 0 3px rgba(249,115,22,0.1)' : 'none' }}>
              <textarea ref={taRef} rows={1} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey} disabled={loading || !authReady} placeholder={authReady ? 'Ask anything about your P&L...' : 'Connecting...'} style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', resize: 'none', color: '#1e293b', fontSize: 13, lineHeight: 1.55, fontFamily: 'inherit', maxHeight: 140, minHeight: 24, paddingTop: 2, cursor: 'text' }} />
              <button onClick={() => { if (input.trim()) send(input) }} disabled={loading || !input.trim() || !authReady} style={{ width: 36, height: 36, background: loading || !input.trim() ? '#e2e8f0' : 'linear-gradient(135deg,#f97316,#ea580c)', border: 'none', borderRadius: 10, color: loading || !input.trim() ? '#94a3b8' : '#fff', cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s', boxShadow: input.trim() && !loading ? '0 4px 12px rgba(249,115,22,0.4)' : 'none' }}>
                {loading ? <div style={{ width: 14, height: 14, border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'pnl-spin 0.8s linear infinite' }} /> : <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></svg>}
              </button>
            </div>
            <p style={{ textAlign: 'center', fontSize: 10, color: '#cbd5e1', marginTop: 6, marginBottom: 0 }}>Enter to send · Shift+Enter for new line</p>
          </div>
        </div>
      )}

      {isMax && (
        <div style={{ position: 'fixed', top: 0, left: 210, right: 0, bottom: 0, zIndex: 9997, display: 'flex', flexDirection: 'column', background: '#fff', fontFamily: FONT, animation: 'pnl-slidein 0.22s cubic-bezier(0.4,0,0.2,1)', boxShadow: '-4px 0 24px rgba(0,0,0,0.08)' }}>
          <HeaderBar large={true} />
          <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
            {showSidebar ? (
              <ChatSidebar sessions={sessions} activeId={sessionId} onSelect={loadSession} onNew={newChat} onDelete={deleteSession} loading={sessLoading} onToggle={() => setShowSidebar(false)} />
            ) : (
              <div style={{ width: 40, flexShrink: 0, background: '#fafafa', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 12, gap: 12 }}>
                <button onClick={() => setShowSidebar(true)} style={{ width: 28, height: 28, borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#f97316', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, transition: 'all 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#fff7ed'; e.currentTarget.style.borderColor = '#f97316' }} onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0' }} title="Show chat history">▶</button>
                <button onClick={newChat} style={{ width: 28, height: 28, borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#f97316', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, transition: 'all 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#fff7ed'; e.currentTarget.style.borderColor = '#f97316' }} onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0' }} title="New chat">+</button>
              </div>
            )}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <MessagesArea large={true} />
              <div style={{ padding: '14px 40px 20px', borderTop: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, background: '#fff', border: '2px solid ' + (input.trim() ? '#f97316' : '#e2e8f0'), borderRadius: 14, padding: '10px 10px 10px 16px', maxWidth: 840, margin: '0 auto', transition: 'border-color 0.2s', boxShadow: input.trim() ? '0 0 0 3px rgba(249,115,22,0.1)' : 'none' }}>
                  <textarea ref={maxTaRef} rows={1} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey} disabled={loading || !authReady} placeholder={authReady ? 'Ask anything about your P&L...' : 'Connecting...'} style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', resize: 'none', color: '#1e293b', fontSize: 15, lineHeight: 1.55, fontFamily: 'inherit', maxHeight: 140, minHeight: 24, paddingTop: 2, cursor: 'text' }} />
                  <button onClick={() => { if (input.trim()) send(input) }} disabled={loading || !input.trim() || !authReady} style={{ width: 42, height: 42, background: loading || !input.trim() ? '#e2e8f0' : 'linear-gradient(135deg,#f97316,#ea580c)', border: 'none', borderRadius: 10, color: loading || !input.trim() ? '#94a3b8' : '#fff', cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s', boxShadow: input.trim() && !loading ? '0 4px 12px rgba(249,115,22,0.4)' : 'none' }}>
                    {loading ? <div style={{ width: 14, height: 14, border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'pnl-spin 0.8s linear infinite' }} /> : <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></svg>}
                  </button>
                </div>
                <p style={{ textAlign: 'center', fontSize: 10, color: '#cbd5e1', marginTop: 6, marginBottom: 0 }}>Enter to send · Shift+Enter for new line</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pnl-bounce  { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-7px)} }
        @keyframes pnl-slidein { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:none} }
        @keyframes pnl-spin    { to{transform:rotate(360deg)} }
      `}</style>
    </>
  )
}